import { useEffect, useState } from "react";
import { api, currency } from "../lib/api";
import NestPie from "../components/NestChart";
import { ArrowUpRight, ArrowDownRight, Wallet, PiggyBank, CloudUpload } from "lucide-react";
import { Link } from "react-router-dom";
import useWealth from "../lib/useWealth";
import { accountValue } from "../lib/prices";
import { getState } from "../lib/vault";
import { daysSince } from "./Settings";
import PriceStatus from "../components/PriceStatus";

const BACKUP_NUDGE_DAYS = 30;

const StatCard = ({ label, value, sub, icon: Icon, tone = "brand", testId }) => {
  const tones = {
    brand: "text-nest-brand bg-nest-brand/10",
    warn: "text-nest-warn bg-nest-warn/10",
    gold: "text-nest-gold-deep bg-nest-gold/20",
  };
  return (
    <div className="nest-card p-4 md:p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-3 md:mb-4">
        <div className="text-[10px] md:text-xs uppercase tracking-widest text-nest-muted leading-tight">
          {label}
        </div>
        <div
          className={`w-8 h-8 md:w-9 md:h-9 shrink-0 rounded-xl flex items-center justify-center ${tones[tone]}`}
        >
          <Icon size={16} strokeWidth={1.5} />
        </div>
      </div>
      <div className="font-heading text-xl sm:text-2xl md:text-3xl tabular text-nest-ink">{value}</div>
      {sub && <div className="text-[11px] md:text-xs text-nest-muted mt-1">{sub}</div>}
    </div>
  );
};

export default function Dashboard() {
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState({ monthly_after_tax: 0 });
  const [debts, setDebts] = useState([]);
  const [wealth, setWealth] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/expenses").then((r) => setExpenses(r.data)),
      api.get("/income").then((r) => setIncome(r.data)),
      api.get("/debts").then((r) => setDebts(r.data)),
      api.get("/wealth").then((r) => setWealth(r.data)),
    ]);
  }, []);

  const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);
  const savings = expenses
    .filter((e) => e.is_savings)
    .reduce((s, e) => s + e.amount, 0);
  const spending = totalExpenses - savings;
  const leftToSpend = income.monthly_after_tax - totalExpenses;

  const latestDebt = debts[debts.length - 1];
  const totalDebt = latestDebt
    ? latestDebt.items.reduce((s, i) => s + i.amount, 0)
    : 0;

  const live = useWealth();
  const latestWealth = wealth[wealth.length - 1];
  const totalWealth = live.total;
  const backupAge = daysSince(getState()?.lastBackupAt);
  const needsBackup = backupAge == null || backupAge >= BACKUP_NUDGE_DAYS;

  // charts
  const expByCategory = Object.values(
    expenses
      .filter((e) => !e.is_savings)
      .reduce((acc, e) => {
        acc[e.category] = acc[e.category] || { name: e.category, value: 0 };
        acc[e.category].value += e.amount;
        return acc;
      }, {})
  );

  const debtChart =
    latestDebt?.items.map((i) => ({ name: i.name, value: i.amount })) || [];
  const wealthChart = live.accounts
    .map((a) => ({ name: a.name, value: accountValue(a, live.quotes).value }))
    .filter((a) => a.value > 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-nest-muted">
          Overview
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-medium mt-2">
          Where things stand.
        </h1>
      </header>

      {needsBackup && (
        <Link
          to="/settings"
          className="flex items-center gap-3 rounded-2xl border border-nest-gold/50 bg-nest-gold/10 px-4 py-3 text-sm"
          data-testid="backup-nudge"
        >
          <CloudUpload size={18} strokeWidth={1.5} className="text-nest-gold-deep shrink-0" />
          <span>
            {backupAge == null
              ? "You haven't backed up yet."
              : `Last backup was ${backupAge} days ago.`}{" "}
            <span className="underline">Back up now</span>
          </span>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <StatCard
          testId="stat-income"
          label="Monthly Income"
          value={currency(income.monthly_after_tax)}
          sub="After tax"
          icon={ArrowUpRight}
          tone="brand"
        />
        <StatCard
          testId="stat-spending"
          label="Monthly Spend"
          value={currency(spending)}
          sub={`+ ${currency(savings)} into savings`}
          icon={ArrowDownRight}
          tone="warn"
        />
        <StatCard
          testId="stat-debt"
          label="Total Debt"
          value={currency(totalDebt)}
          sub={latestDebt ? `as of ${latestDebt.date}` : "—"}
          icon={Wallet}
          tone="warn"
        />
        <StatCard
          testId="stat-wealth"
          label="Wealth Built"
          value={currency(totalWealth)}
          sub={
            live.hasLiveHoldings ? (
              <PriceStatus wealth={live} />
            ) : latestWealth ? (
              `last snapshot ${latestWealth.month}`
            ) : (
              "—"
            )
          }
          icon={PiggyBank}
          tone="gold"
        />
      </div>

      <div className="nest-card p-6 md:p-8" data-testid="left-to-spend-card">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-nest-muted">
              Left to spend
            </div>
            <div
              className={`font-heading text-4xl tabular mt-2 ${
                leftToSpend >= 0 ? "text-nest-good" : "text-nest-warn"
              }`}
            >
              {currency(leftToSpend)}
            </div>
          </div>
          <div className="text-sm text-nest-muted max-w-xs">
            After {currency(totalExpenses)} in monthly outgoings (including{" "}
            {currency(savings)} routed to savings & car fund).
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="nest-card p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Expenses by category
          </div>
          <NestPie data={expByCategory} testId="dashboard-expenses-chart" height={280} />
        </div>
        <div className="nest-card p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Debt breakdown
          </div>
          <NestPie data={debtChart} testId="dashboard-debt-chart" height={280} />
        </div>
        <div className="nest-card p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Wealth allocation
          </div>
          <NestPie data={wealthChart} testId="dashboard-wealth-chart" height={280} />
        </div>
      </div>
    </div>
  );
}
